import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── SatisfactionStep ────────────────────────────────────────────────────────

const SATISFACTION_OPTIONS = [
  { emoji: '😍', label: 'Loving it', value: 'loving_it' },
  { emoji: '😊', label: 'Good', value: 'good' },
  { emoji: '😐', label: 'Okay', value: 'okay' },
  { emoji: '😕', label: 'Not great', value: 'not_great' },
  { emoji: '😞', label: 'Frustrated', value: 'frustrated' },
] as const;

interface SatisfactionStepProps {
  value: string;
  onChange: (value: string) => void;
}

export function SatisfactionStep({ value, onChange }: SatisfactionStepProps) {
  return (
    <div className="max-w-sm mx-auto space-y-6">
      <h3 className="text-lg font-semibold text-center text-foreground">
        How's your experience so far?
      </h3>
      {/* 25/09: 5 opções numa fileira não cabiam em 375px e alargavam a janela
          (o "Next" saía cortado). Agora quebram em duas linhas. */}
      <div className="flex flex-wrap justify-center gap-2">
        {SATISFACTION_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                'flex min-h-12 min-w-[96px] flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200',
                isSelected
                  ? 'bg-accent/15 ring-2 ring-accent'
                  : 'bg-card/50 border border-border/40 hover:border-border'
              )}
            >
              <span className="text-2xl" role="img" aria-label={option.label}>
                {option.emoji}
              </span>
              <span className="text-[15px] font-medium text-muted-foreground whitespace-nowrap">
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── PurchaseStep ────────────────────────────────────────────────────────────

const PURCHASE_OPTIONS = [
  { emoji: '😊', label: 'Smooth and easy', value: 'smooth' },
  { emoji: '😐', label: 'A bit confusing', value: 'confusing' },
  { emoji: '😕', label: 'Had trouble finding the app', value: 'trouble_finding' },
  { emoji: '😟', label: 'Almost gave up', value: 'almost_gave_up' },
] as const;

interface PurchaseStepProps {
  value: string;
  onChange: (value: string) => void;
  details: string;
  onDetailsChange: (value: string) => void;
}

export function PurchaseStep({ value, onChange, details, onDetailsChange }: PurchaseStepProps) {
  const showDetails = value !== '' && value !== 'smooth';

  return (
    <div className="max-w-sm mx-auto space-y-6">
      <h3 className="text-lg font-semibold text-center text-foreground">
        How was your purchase experience?
      </h3>
      <div className="space-y-3">
        {PURCHASE_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200',
                isSelected
                  ? 'bg-orange-500/20 ring-2 ring-orange-500'
                  : 'bg-card/50 border border-border/40 hover:border-border'
              )}
            >
              <span className="text-xl" role="img" aria-label={option.label}>
                {option.emoji}
              </span>
              <span className="text-sm font-medium text-foreground">
                {option.label}
              </span>
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <textarea
              value={details}
              onChange={(e) => onDetailsChange(e.target.value)}
              placeholder="What was confusing? (optional)"
              rows={3}
              className={cn(
                'w-full mt-2 p-3 rounded-xl text-sm resize-none',
                'bg-card/50 border border-border/40 text-foreground placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent'
              )}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── FeatureRequestStep ──────────────────────────────────────────────────────

const FEATURE_OPTIONS = [
  { emoji: '📱', label: 'More scripts', value: 'more_scripts' },
  { emoji: '💬', label: 'Longer conversations with Sarah', value: 'longer_sarah' },
  { emoji: '👨‍👩‍👧', label: 'Family sharing', value: 'family_sharing' },
  { emoji: '📊', label: 'Progress tracking/reports', value: 'progress_tracking' },
  { emoji: '🧠', label: 'More brain profile insights', value: 'brain_insights' },
  { emoji: '📚', label: 'More programs/challenges', value: 'more_programs' },
] as const;

const MAX_FEATURES = 3;

interface FeatureRequestStepProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export function FeatureRequestStep({ value, onChange }: FeatureRequestStepProps) {
  const handleToggle = (featureValue: string) => {
    if (value.includes(featureValue)) {
      onChange(value.filter((v) => v !== featureValue));
    } else if (value.length < MAX_FEATURES) {
      onChange([...value, featureValue]);
    }
  };

  return (
    <div className="max-w-sm mx-auto space-y-6">
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold text-foreground">
          What would help you the most?
        </h3>
        <p className="text-sm text-muted-foreground">
          {value.length}/{MAX_FEATURES} selected
        </p>
      </div>
      <div className="space-y-3">
        {FEATURE_OPTIONS.map((option) => {
          const isSelected = value.includes(option.value);
          const isDisabled = !isSelected && value.length >= MAX_FEATURES;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={isDisabled}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200',
                isSelected
                  ? 'bg-orange-500/20 ring-2 ring-orange-500'
                  : 'bg-card/50 border border-border/40 hover:border-border',
                isDisabled && 'opacity-40 cursor-not-allowed'
              )}
            >
              <span className="text-xl" role="img" aria-label={option.label}>
                {option.emoji}
              </span>
              <span className="flex-1 text-sm font-medium text-foreground">
                {option.label}
              </span>
              {isSelected && (
                <Check className="w-5 h-5 text-orange-500 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── OpenFeedbackStep ────────────────────────────────────────────────────────

const WORD_THRESHOLD = 30;

interface OpenFeedbackStepProps {
  value: string;
  onChange: (value: string) => void;
  wordCount: number;
}

export function OpenFeedbackStep({ value, onChange, wordCount }: OpenFeedbackStepProps) {
  const isUnlocked = wordCount >= WORD_THRESHOLD;
  const progress = Math.min(wordCount / WORD_THRESHOLD, 1);

  return (
    <div className="max-w-sm mx-auto space-y-6">
      <h3 className="text-lg font-semibold text-center text-foreground">
        Tell us what we can improve
      </h3>

      {/* Reward banner */}
      <div
        className={cn(
          'p-4 rounded-xl text-center text-sm font-medium transition-colors duration-500',
          isUnlocked
            ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30'
            : 'bg-orange-500/20 text-orange-400 ring-1 ring-orange-500/30'
        )}
      >
        {isUnlocked
          ? '✅ Early access unlocked!'
          : '🎁 Share detailed feedback and unlock early access to new features'}
      </div>

      {/* Early access reward image */}
      <div className="rounded-xl overflow-hidden">
        <img loading="lazy" decoding="async"
          src="/images/early-access-reward.jpg"
          alt="Early access reward"
          className="w-full h-auto object-cover rounded-xl"
        />
      </div>

      {/* Textarea */}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Tell us what's working, what's not, and what you wish the app had..."
        rows={5}
        className={cn(
          'w-full p-4 rounded-xl text-sm resize-none',
          'bg-card/50 border border-border/40 text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent'
        )}
      />

      {/* Word counter */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{wordCount} / {WORD_THRESHOLD} words for early access</span>
          {isUnlocked && <span className="text-emerald-400 font-medium">Unlocked!</span>}
        </div>

        {/* Progress bar */}
        <div className="h-2 rounded-full bg-card/50 border border-border/40 overflow-hidden">
          <motion.div
            className={cn(
              'h-full rounded-full',
              isUnlocked ? 'bg-emerald-500' : 'bg-orange-500'
            )}
            initial={{ width: 0 }}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </div>
    </div>
  );
}
